import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Grid,
  Paper,
} from "@mui/material";
import { useSelector } from "react-redux";
import ArrowBackIcon from "../../../../assets/icons/back.svg";
import SendIcon from "../../../../assets/icons/send-quotation-icon.svg";
import CalendarIcon from "../../../../assets/icons/book-service-icon.svg";
import { getBookingAppointmentLeadById } from "../../../../api/provider-section/provider-service-management";
import SendQuotationModal from "../send-quotation-modal";
import BookService from "../book-services";
import "./lead-details.scss";
import LeadDetailsInformationForm from "./lead-details-information-form";
import { CreateOutlined } from "@mui/icons-material";
import LeadAddressDetailsInformationForm from "./lead-address-details-information-form";
import ProfileHeader from "../../../../common/components/profile-header/profile-header-and-breadcrumbs";
import { routes } from "../../../../constants/routes";
import { useSetLoading } from "../../../../common/hooks/appearance";
interface LeadDetailsProps {
  leadId: number;
  onBack: () => void;
}

export interface LeadData {
  id: number;
  name: string;
  email: string;
  phone: string;
  needType: string;
  leadSource: string;
  leadStatus: string;
  address?: {
    street_line_1: string;
    street_line_2: string;
    city: string;
    state: string;
    zipcode: string;
    country: string;
  };
}

const getInterestedProductLabel = (needType: unknown): string => {
  if (needType === null || needType === undefined) return "";

  const mapSingle = (value: string) => {
    const normalized = value
      .toLowerCase()
      .replace(/[_-]/g, " ")
      .replace(/\s+/g, " ")
      .trim();

    if (["at need", "atneed", "immediate need"].includes(normalized)) {
      return "Immediate Need";
    }
    if (["pre need", "preneed", "preneed planning"].includes(normalized)) {
      return "Preneed";
    }
    return value.trim();
  };

  const items = String(needType)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

  if (items.length === 0) return "";
  return items.map(mapSingle).join(", ");
};

const LeadDetails: React.FC<LeadDetailsProps> = ({ leadId, onBack }) => {
  const location = useLocation();
  const [breadcrumbLink, setBreadcrumbLink] = useState(() => {
    return (location.state?.breadcrumbLink || []).map((item: any) => {
      if (item.label === "Leads" || item.label === "Leads List") {
        return { ...item, link: routes.PROVIDER_LEADS };
      }
      return item;
    });
  });
  const [lead, setLead] = useState<LeadData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sendQuotationOpen, setSendQuotationOpen] = useState(false);
  const [showBookService, setShowBookService] = useState(false);
  const [addressEdit, setAddressEdit] = useState(false);

  // Callback to update lead name in breadcrumb
  const updateLeadNameInBreadcrumb = (newName: string) => {
    setBreadcrumbLink((prev: any) =>
      prev.map((item: any, idx: number) => {
        // Find the last breadcrumb (lead name is always last)
        if (idx === prev.length - 1) {
          return { ...item, label: newName };
        }
        return item;
      }),
    );
  };

  const partnerIdFromRedux = useSelector(
    (state: any) => state?.session?.providerDetails?.provider?.partnerId,
  );
  const partnerId = useMemo(() => {
    if (partnerIdFromRedux != null) return partnerIdFromRedux;
    try {
      const raw = localStorage.getItem("providerDetails");
      const parsed = raw ? JSON.parse(raw) : {};
      return parsed?.provider?.partnerId ?? null;
    } catch {
      return null;
    }
  }, [partnerIdFromRedux]);
  const fetchLeadDetails = async () => {
    setLoading(true);
    try {
      const response = await getBookingAppointmentLeadById(leadId);
      if (response.data?.success) {
        const leadData = response.data?.data?.lead || response.data?.data;
        if (leadData) {
          const mappedLead: LeadData = {
            id: leadData.id,
            name: leadData.name || leadData.leadName || "",
            email: leadData.email || "",
            phone: leadData.phone || leadData.phoneNumber || "",
            needType: getInterestedProductLabel(leadData.needType),
            leadSource: leadData.leadSource || "",
            leadStatus: leadData.leadStatus || leadData.status || "",
            address: leadData.address || undefined,
          };
          console.log("mapped lead", mappedLead)
          setLead(mappedLead);
        } else {
          setError("Lead data not found");
        }
      } else {
        setError("Failed to fetch lead details");
      }
    } catch (err) {
      console.error("Error fetching lead details:", err);
      setError("An error occurred while fetching lead details");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchLeadDetails();
  }, [leadId]);
  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);
  // if (loading) {
  //   return (
  //     <Box display="flex" justifyContent="center" alignItems="center" p={4}>
  //       <CircularProgress size={40} />
  //     </Box>
  //   );
  // }

  if (loading && !lead) {
    return "Loading";
  }

  if (!loading && (error || !lead)) {
    return (
      <Box p={4} textAlign="center">
        {/* <Typography color="error">{error || "Lead not found"}</Typography> */}
        <Button onClick={onBack} sx={{ mt: 2 }}>
          Back to Leads
        </Button>
      </Box>
    );
  }

  if (showBookService) {
    return (
      <BookService leadId={leadId} onBack={() => setShowBookService(false)} />
    );
  }

  const infoItems = [
    { label: "Full Name", value: lead?.name },
    { label: "Email Address", value: lead?.email },
    { label: "Phone Number", value: lead?.phone },
    { label: "Interested Product", value: lead?.needType },
    { label: "Lead Source", value: lead?.leadSource },
    { label: "Status", value: lead?.leadStatus },
  ];

  const addressItems = [
    { label: "Street Line 1", value: lead?.address?.street_line_1 },
    { label: "Street Line 2", value: lead?.address?.street_line_2 },
    { label: "City", value: lead?.address?.city },
    { label: "State", value: lead?.address?.state },
    { label: "Country", value: lead?.address?.country },
    { label: "Zipcode", value: lead?.address?.zipcode },
  ];
  return (
    <div className="lead-details-main-wrapper">
      <div
        style={{
          flexDirection: "column",
          height: "100%",
        }}
      >
        <ProfileHeader breadcrumbLink={breadcrumbLink} mode="provider" />

        <Box className="lead-details">
          {/* <Box className="lead-details__back" onClick={onBack}>
            <img
              src={ArrowBackIcon}
              alt="back"
              className="lead-details__back-icon"
            />
            <Typography variant="body1" className="lead-details__back-text">
              Back to Leads
            </Typography>
          </Box> */}
          <LeadDetailsInformationForm
            data={lead}
            editMode={false}
            innerRef={null}
            infoItems={infoItems}
            fetchLeadDetails={fetchLeadDetails}
            updateLeadNameInBreadcrumb={updateLeadNameInBreadcrumb}
          />

          {!addressEdit && (
            <Paper elevation={0} className="lead-details__section">
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="h6"
                  className="lead-details__section-title"
                >
                  Address Information
                </Typography>
                <div className="lead-details-section-edit-section">
                  <CreateOutlined
                    onClick={() => {
                      if (
                        lead?.leadStatus !== "Closed-Won" &&
                        lead?.leadStatus !== "Closed-Lost"
                      ) {
                        setAddressEdit(true);
                      }
                    }}
                    className={`lead-details-section-edit-icon ${lead?.leadStatus === "Closed-Won" ||
                      lead?.leadStatus === "Closed-Lost"
                      ? "disabled"
                      : ""
                      }`}
                    fontSize="small"
                    style={{
                      cursor:
                        lead?.leadStatus === "Closed-Won" ||
                          lead?.leadStatus === "Closed-Lost"
                          ? "not-allowed"
                          : "pointer",
                      opacity:
                        lead?.leadStatus === "Closed-Won" ||
                          lead?.leadStatus === "Closed-Lost"
                          ? 0.5
                          : 1,
                      marginRight: "-4px",
                      marginTop: "-13px",
                    }}
                  />
                </div>
              </div>
              <Grid container spacing={3}>
                {addressItems.map((item, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Typography
                      variant="body2"
                      className="lead-details__item-label"
                    >
                      {item.label}
                    </Typography>
                    <Typography
                      variant="body1"
                      className="lead-details__item-value"
                    >
                      {item.value || "-"}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          )}
          {addressEdit && (
            <LeadAddressDetailsInformationForm
              addressEdit={addressEdit}
              setAddressEdit={setAddressEdit}
              lead={lead}
              fetchLeadDetails={fetchLeadDetails}
            />
          )}
          <Box className="lead-details__actions">
            <Button
              variant="contained"
              startIcon={<img src={SendIcon} alt="send" />}
              className="lead-details__btn lead-details__btn-send"
              onClick={() => setSendQuotationOpen(true)}
              disabled={!partnerId}
            >
              Send Quotation
            </Button>
            <Button
              variant="contained"
              startIcon={<img src={CalendarIcon} alt="calendar" />}
              className="lead-details__btn lead-details__btn-book"
              onClick={() => setShowBookService(true)}
            >
              Order Service
            </Button>
          </Box>

          {partnerId != null && (
            <SendQuotationModal
              open={sendQuotationOpen}
              onClose={() => setSendQuotationOpen(false)}
              partnerId={Number(partnerId)}
              leadId={leadId}
              leadName={lead.name}
              leadEmail={lead.email}
              onSuccess={() => {
                // Optional: refresh lead or show toast
              }}
            />
          )}
        </Box>
      </div>
    </div>
  );
};

export default LeadDetails;
